import React, { useEffect, useRef, useState } from 'react';
import { Archive, ArchiveRestore, MoreVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import '../../Components/ComponentActionsDropdown.css';

interface KeyTypeActionsMenuProps {
    archived: boolean;
    canDelete: boolean;
    disabled?: boolean;
    onEdit: () => void;
    /** Lägg till exemplar; not offered on an archived type. */
    onAddKeys: () => void;
    onToggleArchive: () => void;
    onDelete: () => void;
}

/** Three-dot menu on a key type row: Redigera, Lägg till exemplar, Arkivera/Återställ, and Ta bort for a type without keys. */
const KeyTypeActionsMenu: React.FC<KeyTypeActionsMenuProps> = ({ archived, canDelete, disabled = false, onEdit, onAddKeys, onToggleArchive, onDelete }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const outside = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const escape = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
        document.addEventListener('mousedown', outside);
        document.addEventListener('keydown', escape, true);
        return () => {
            document.removeEventListener('mousedown', outside);
            document.removeEventListener('keydown', escape, true);
        };
    }, [open]);

    const pick = (action: () => void) => () => { setOpen(false); action(); };

    return (
        <div ref={ref} className="actions-dropdown key-type-actions">
            <button type="button" className={`actions-dropdown-button key-type-actions-button ${open ? 'actions-dropdown-button--active' : ''}`}
                onClick={() => setOpen(o => !o)} disabled={disabled} title="Åtgärder" aria-label="Åtgärder" aria-haspopup="menu" aria-expanded={open}>
                <MoreVertical size={18} color="#666" />
            </button>
            {open && (
                <div className="actions-dropdown-menu" role="menu">
                    <button type="button" role="menuitem" className="actions-dropdown-item" onClick={pick(onEdit)}>
                        <Pencil size={18} color="#6c757d" /><span>Redigera</span>
                    </button>
                    {!archived && (
                        <button type="button" role="menuitem" className="actions-dropdown-item" onClick={pick(onAddKeys)}>
                            <Plus size={18} color="#6c757d" /><span>Lägg till exemplar</span>
                        </button>
                    )}
                    <button type="button" role="menuitem" className="actions-dropdown-item" onClick={pick(onToggleArchive)}>
                        {archived ? <ArchiveRestore size={18} color="#6c757d" /> : <Archive size={18} color="#6c757d" />}
                        <span>{archived ? 'Återställ' : 'Arkivera'}</span>
                    </button>
                    {canDelete && (
                        <button type="button" role="menuitem" className="actions-dropdown-item text-danger" onClick={pick(onDelete)}>
                            <Trash2 size={18} /><span>Ta bort</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default KeyTypeActionsMenu;
